import { useMemo } from "react";
import {
  Chart as ChartJS,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
  type ChartData,
  type ChartOptions,
  type ScriptableScaleContext,
  type TooltipItem,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { FORECAST_ZONE_PLUGIN_ID, forecastZonePlugin, type ForecastZoneOptions } from "./forecastZonePlugin";
import { markerPaletteEntry } from "./markerPalette";
import { monthLabelStep, shortMonthLabel, spansMoreThanOneYear } from "./monthAxis";
import { readRsChartColors } from "./rsChartColors";
import { createVerticalHoverLinePlugin } from "./verticalHoverLinePlugin";

ChartJS.register(LinearScale, PointElement, LineElement, Tooltip);

export interface MonthlyLinePoint {
  monthIndex: number;
  value: number;
  tooltip: string;
}

export interface MonthlyMarker {
  x: number;
  value: number;
  tooltip: string;
  paletteIndex?: number;
}

export interface MonthlyForecast {
  label: string;
  startMonthIndex: number;
  anchor: { monthIndex: number; value: number } | null;
  points: MonthlyLinePoint[];
}

interface Props {
  months: string[];
  highlightedMonth?: string;
  line: { label: string; points: MonthlyLinePoint[] };
  markers?: { label: string; points: MonthlyMarker[]; onMarkerClick?: (markerIndex: number) => void };
  forecast?: MonthlyForecast | null;
  formatValue: (value: number) => string;
  ariaLabel: string;
}

interface ChartPosition {
  x: number;
  y: number;
}

export const LINE_DATASET_INDEX = 0;
export const MARKER_DATASET_INDEX = 1;
export const FORECAST_DATASET_INDEX = 2;

export function MonthlyTrendChart({
  months,
  highlightedMonth,
  line,
  markers,
  forecast,
  formatValue,
  ariaLabel,
}: Props) {
  const colors = useMemo(() => readRsChartColors(), []);
  const hoverLinePlugin = useMemo(() => createVerticalHoverLinePlugin<"line">(colors.offWhite), [colors]);
  const labelStep = monthLabelStep(months.length);
  const showYear = spansMoreThanOneYear(months);
  const markerPoints = markers?.points ?? [];
  const onMarkerClick = markers?.onMarkerClick;
  const forecastPoints = forecast?.points ?? [];
  const forecastAnchorCount = forecast?.anchor ? 1 : 0;

  const data: ChartData<"line", ChartPosition[]> = {
    datasets: [
      {
        label: line.label,
        data: line.points.map((point) => ({ x: point.monthIndex, y: point.value })),
        borderColor: colors.green,
        backgroundColor: colors.green,
        borderWidth: 3,
        tension: 0.3,
        pointRadius: 3,
        pointHoverRadius: 5,
      },
      {
        label: markers?.label ?? "",
        data: markerPoints.map((marker) => ({ x: marker.x, y: marker.value })),
        showLine: false,
        pointRadius: 5,
        pointHoverRadius: 7,
        pointBackgroundColor: markerPoints.map((marker) => colors[markerPaletteEntry(marker.paletteIndex ?? 0).chartColor]),
        pointHoverBackgroundColor: colors.green,
        pointBorderColor: colors.black,
        pointBorderWidth: 1.5,
      },
      {
        label: forecast?.label ?? "",
        data: [
          ...(forecast?.anchor ? [{ x: forecast.anchor.monthIndex, y: forecast.anchor.value }] : []),
          ...forecastPoints.map((point) => ({ x: point.monthIndex, y: point.value })),
        ],
        borderColor: colors.green,
        borderWidth: 2,
        borderDash: [6, 5],
        tension: 0.3,
        pointBackgroundColor: colors.black,
        pointBorderColor: colors.green,
        pointBorderWidth: 2,
        pointRadius: (context) => (context.dataIndex < forecastAnchorCount ? 0 : 4),
        pointHoverRadius: (context) => (context.dataIndex < forecastAnchorCount ? 0 : 6),
        pointHitRadius: (context) => (context.dataIndex < forecastAnchorCount ? 0 : 4),
      },
    ],
  };

  const isHighlightedTick = (context: ScriptableScaleContext): boolean => {
    const tickValue = (context as Partial<ScriptableScaleContext>).tick?.value;
    return tickValue !== undefined && months[tickValue] === highlightedMonth;
  };

  const forecastZoneOptions: Record<string, ForecastZoneOptions> = {
    [FORECAST_ZONE_PLUGIN_ID]: forecast
      ? {
          startValue: forecast.startMonthIndex - 0.5,
          label: "Forecast →",
          fillColor: colors.green,
          labelColor: colors.lightGray,
        }
      : {},
  };

  const tooltipLabel = (item: TooltipItem<"line">): string => {
    if (item.datasetIndex === LINE_DATASET_INDEX) return line.points[item.dataIndex].tooltip;
    if (item.datasetIndex === MARKER_DATASET_INDEX) return markerPoints[item.dataIndex].tooltip;
    return forecastPoints[item.dataIndex - forecastAnchorCount]?.tooltip ?? "";
  };

  const options: ChartOptions<"line"> = {
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: { top: 12, right: 12, bottom: 4, left: 4 } },
    interaction: { mode: "nearest", intersect: true },
    onHover: (event, activeElements) => {
      const canvas = event.native?.target;
      if (!(canvas instanceof HTMLCanvasElement)) return;
      const isOverClickableMarker =
        Boolean(onMarkerClick) && activeElements.some((element) => element.datasetIndex === MARKER_DATASET_INDEX);
      canvas.style.cursor = isOverClickableMarker ? "pointer" : "default";
    },
    onClick: (_event, activeElements) => {
      const markerElement = activeElements.find((element) => element.datasetIndex === MARKER_DATASET_INDEX);
      if (markerElement && onMarkerClick) onMarkerClick(markerElement.index);
    },
    plugins: {
      ...forecastZoneOptions,
      legend: { display: false },
      tooltip: {
        backgroundColor: colors.offBlack,
        titleColor: colors.offWhite,
        bodyColor: colors.offWhite,
        borderColor: colors.jetLight,
        borderWidth: 1,
        padding: 8,
        cornerRadius: 8,
        displayColors: false,
        callbacks: { title: () => "", label: tooltipLabel },
      },
    },
    scales: {
      x: {
        type: "linear",
        min: -0.5,
        max: months.length - 0.5,
        afterBuildTicks: (axis) => {
          axis.ticks = months.map((_, monthIndex) => ({ value: monthIndex }));
        },
        grid: { color: colors.jet },
        ticks: {
          color: (context) => (isHighlightedTick(context) ? colors.offWhite : colors.lightGray),
          autoSkip: false,
          maxRotation: 0,
          font: (context) => ({ size: 11, weight: isHighlightedTick(context) ? "bold" : "normal" }),
          callback: (tickValue) => {
            const monthIndex = Number(tickValue);
            const month = months[monthIndex];
            if (!month || (monthIndex % labelStep !== 0 && month !== highlightedMonth)) return null;
            return shortMonthLabel(month, showYear);
          },
        },
      },
      y: {
        grace: "5%",
        grid: { color: colors.jet },
        ticks: {
          color: colors.lightGray,
          font: { size: 11 },
          callback: (tickValue) => formatValue(Number(tickValue)),
        },
      },
    },
  };

  return (
    <div className="h-72 w-full">
      <Line
        data={data}
        options={options}
        plugins={[hoverLinePlugin, forecastZonePlugin]}
        role="img"
        aria-label={ariaLabel}
      />
    </div>
  );
}
